import { styled } from "goober";
import { useState } from "react";
import { formatTinesUnits } from "../../lib/formatTinesUnits";
import {
  ChartTooltipCount,
  ChartTooltipHeader,
  ChartTooltipRow,
} from "../chartHover";
import { FadeScrollColumns } from "./monitoringBars";
import { TimeSeriesChart } from "./TimeSeriesChart";
import { unitsMonthLabel } from "./tinesUnitsChartLayout";
import type { TinesUnitsTrendChart } from "./tinesUnitsTrendRows";

const monthLabel = new Intl.DateTimeFormat("en-US", {
  month: "short",
  timeZone: "UTC",
});

const Chart = styled("div")`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;

  & > div {
    min-height: 0;
    width: 100%;
    max-width: none;
  }
`;

export function TinesUnitsLineChart({
  columns,
  limit,
  selectedPeriodStart,
  onSelectMonth,
}: {
  columns: TinesUnitsTrendChart["columns"];
  limit: number;
  selectedPeriodStart: number;
  onSelectMonth: (periodStart: number) => void;
}) {
  const [overlayContainer, setOverlayContainer] =
    useState<HTMLDivElement | null>(null);
  const currentIndex = columns.findIndex((column) => column.isCurrent);
  return (
    <Chart ref={setOverlayContainer}>
      <FadeScrollColumns>
        <TimeSeriesChart
          points={columns.map((column) => ({
            key: column.periodStart,
            value: column.total,
            projectedValue: column.projectedTotal,
            label: monthLabel.format(column.periodStart),
          }))}
          projection={
            currentIndex >= 0
              ? { fromIndex: currentIndex - 1, mode: "values" }
              : undefined
          }
          referenceLine={limit}
          overlayContainer={overlayContainer}
          minWidth={Math.max(140, columns.length * 44 - 8)}
          selection={{
            index: columns.findIndex(
              (column) => column.periodStart === selectedPeriodStart
            ),
            label: (index) =>
              `View space usage for ${unitsMonthLabel.format(columns[index]!.periodStart)}`,
            onSelect: (index) => onSelectMonth(columns[index]!.periodStart),
          }}
          tooltipWidth={176}
          tooltip={(index) => {
            const column = columns[index]!;
            const isProjected =
              column.isCurrent && column.projectedTotal != null;
            const overLimit =
              (isProjected ? column.projectedTotal! : column.total) - limit;
            return (
              <>
                <ChartTooltipHeader>
                  {unitsMonthLabel.format(column.periodStart)}
                </ChartTooltipHeader>
                <ChartTooltipRow>
                  {column.isCurrent ? "This month so far" : "Tines units"}
                  <ChartTooltipCount>
                    {formatTinesUnits(column.total)}
                  </ChartTooltipCount>
                </ChartTooltipRow>
                {column.isCurrent && (
                  <ChartTooltipRow>
                    Projected
                    <ChartTooltipCount>
                      {column.projectedTotal == null
                        ? "—"
                        : formatTinesUnits(column.projectedTotal)}
                    </ChartTooltipCount>
                  </ChartTooltipRow>
                )}
                {overLimit > 0 && (
                  <ChartTooltipRow style={{ color: "var(--red-600)" }}>
                    {isProjected ? "Projected over limit" : "Over limit"}
                    <ChartTooltipCount>
                      {formatTinesUnits(overLimit)}
                    </ChartTooltipCount>
                  </ChartTooltipRow>
                )}
              </>
            );
          }}
        />
      </FadeScrollColumns>
    </Chart>
  );
}
